/**
 * @file Build-time environment for the SPA.
 *
 * Vite statically replaces `import.meta.env.*` during the build, so a malformed
 * value is a BUILD failure rather than a request-time surprise.
 *
 * There is now exactly one setting, and it is optional. The SPA calls the API
 * SAME-ORIGIN under `/api`: Better Auth's session is a first-party cookie, and a browser
 * attaches it only to requests to the page's own origin (Safari drops it for any other).
 * Locally the Vite dev server proxies `/api` to the API; in staging a Worker route on the
 * SPA's hostname does the same. So the default needs no configuration at all, and there is
 * no longer an auth key to supply, no "local auth" switch, and nothing that has to be set
 * before the first deploy.
 *
 * Why this is not zod (unlike the Worker's `@neighboros/config`): the Worker validates a
 * *dynamic* bindings object at boot; the SPA validates one string the bundler already
 * inlined. A schema library would ship to every browser to check one constant, so the check
 * is written by hand and kept pure (`readWebEnv` takes the raw record, so tests exercise it
 * without touching `import.meta.env`).
 *
 * SECURITY: every `VITE_*` value is inlined into the shipped JavaScript and is readable by
 * anyone. Nothing secret may ever be added here — no auth secret, no Stripe key, no DSN with
 * write access. Keys of that class live only in the Worker's `wrangler secret` store
 * (KEYS.md, scope `founder-only`).
 */

/** The validated, application-visible environment. */
export interface WebEnv {
  /**
   * Where the API is, with any trailing slash removed: a same-origin path (`/api`, the
   * default) or, for a test, an absolute `http(s)` origin. Concatenated as `${base}/me`.
   */
  readonly apiBaseUrl: string;
}

/** The raw record Vite exposes on `import.meta.env`. */
export interface RawWebEnv {
  readonly VITE_API_BASE_URL?: string | undefined;
}

/**
 * The default: the API under `/api` on the page's own origin.
 *
 * Overriding it with a DIFFERENT origin builds a bundle whose requests are cross-origin, so
 * the session cookie is not sent and every call is a 401. That is allowed (a test needs an
 * absolute URL) but it is not a supported way to deploy.
 */
export const DEFAULT_API_BASE_URL = "/api";

/** Strip trailing slashes so paths can always be concatenated as `${base}/me`. */
function normaliseBaseUrl(value: string): string {
  return value.replace(/\/+$/, "");
}

/**
 * Validate the build-time environment, throwing a message that names the exact variable to
 * set. Called once from `main.tsx` before React mounts.
 */
export function readWebEnv(raw: RawWebEnv): WebEnv {
  const rawBaseUrl = (raw.VITE_API_BASE_URL ?? "").trim();
  const apiBaseUrl = normaliseBaseUrl(rawBaseUrl || DEFAULT_API_BASE_URL);

  const sameOriginPath = apiBaseUrl.startsWith("/") && !apiBaseUrl.startsWith("//");
  const absoluteOrigin = /^https?:\/\//.test(apiBaseUrl);
  if (!sameOriginPath && !absoluteOrigin) {
    throw new Error(
      "VITE_API_BASE_URL must be a same-origin path such as \"/api\" (the default) or an " +
        `http(s) origin, received "${apiBaseUrl}".`,
    );
  }

  return { apiBaseUrl };
}
